import { Alert, Button, Modal, Skeleton, Tag, Tooltip } from 'antd';
import {
  CalendarOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { ContentPreviewModal } from 'components/modal/content-tree/ContentPreviewModal';
import { ISupplementaryMaterial } from 'interfaces/supplementary_materials';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { SupplementarMaterialUploadModal } from 'components/modal/professor_area/classroom/SupplementarMaterialUploadModal';
import {
  download_url as downloadSupplementaryMaterials,
  list as listSupplementaryMaterials
} from 'requests/supplementary-materials';
import { formatDate } from 'services/date';
import { useDelete } from '@refinedev/core';
import { useModal, useModalForm } from '@refinedev/antd';

export type SupplementaryMaterialParentKind =
  | 'course'
  | 'graduate_course'
  | 'classroom';

export type SupplementaryMaterialsVariant = 'institutional' | 'complementary';

const COPY: Record<
  SupplementaryMaterialsVariant,
  {
    description: string;
    addButtonText: string;
    uploadOkText: string;
    uploadSuccess: string;
    deleteSuccess: string;
    deleteError: string;
  }
> = {
  institutional: {
    description: 'Disponibilize documentos em PDF para os alunos deste curso.',
    addButtonText: 'Cadastrar material',
    uploadOkText: 'Cadastrar material',
    uploadSuccess: 'Material institucional enviado com sucesso!',
    deleteSuccess: 'Material institucional excluído com sucesso!',
    deleteError: 'Não foi possível excluir o material institucional.'
  },
  complementary: {
    description: 'Disponibilize arquivos para os alunos desta turma.',
    addButtonText: 'Adicionar Material',
    uploadOkText: 'Publicar material',
    uploadSuccess: 'Material complementar enviado com sucesso!',
    deleteSuccess: 'Material complementar excluído com sucesso!',
    deleteError: 'Não foi possível excluir o material complementar.'
  }
};

export interface SupplementaryMaterialsPublication {
  published: boolean;
  isLoading: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
}

interface SupplementaryMaterialsPanelProps {
  parentKind: SupplementaryMaterialParentKind;
  parentReference: string;
  variant: SupplementaryMaterialsVariant;
  publication?: SupplementaryMaterialsPublication;
}

const { confirm } = Modal;

// O preview_url é pré-assinado pelo Atlas com content-type application/pdf
// fixo; qualquer outro tipo não renderiza no iframe de preview
// (ContentPreviewModal -> IframeContent), então só PDF é visualizável e os
// demais ficam disponíveis apenas para download.
const PREVIEWABLE_EXTENSIONS = new Set(['pdf']);

const getFileExtension = (url: string) => {
  const withoutQuery = url.split(/[#?]/)[0];
  const segments = withoutQuery.split('.');
  return segments.length > 1 ? segments.pop()!.toLowerCase() : '';
};

const isPreviewable = (url: string) =>
  PREVIEWABLE_EXTENSIONS.has(getFileExtension(url));

const FileThumb = () => (
  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-500/10">
    <FileOutlined className="text-lg !text-primary-500" />
  </div>
);

interface IconActionButtonProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

const IconActionButton = ({
  label,
  icon,
  onClick,
  danger,
  disabled
}: IconActionButtonProps) => (
  <Tooltip title={label}>
    <Button
      size="small"
      danger={danger}
      icon={icon}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    />
  </Tooltip>
);

interface MaterialCardProps {
  material: ISupplementaryMaterial;
  onPreview: (fileUrl: string) => void;
  onDownload: (id: string) => void;
  onDelete: (id: string) => void;
}

const MaterialCard = ({
  material,
  onPreview,
  onDownload,
  onDelete
}: MaterialCardProps) => {
  const fileUrl = material.preview_url || material.url;
  const previewable = isPreviewable(fileUrl);
  const extension = getFileExtension(fileUrl);

  return (
    <article className="flex flex-col rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
      <div className="flex min-w-0 items-start gap-3">
        <FileThumb />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-sm font-semibold text-gray-900">
            {material.title}
          </p>
          {extension && (
            <p className="m-0 mt-0.5 text-xs uppercase text-gray-400">
              {extension}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-neutral-200 pt-3">
        <div className="flex min-w-0 items-center gap-2 text-xs text-gray-400">
          <CalendarOutlined />
          <span className="truncate">
            Publicado em{' '}
            {formatDate(material.created_at, 'D [de] MMM [de] YYYY')}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <IconActionButton
            label={
              previewable
                ? 'Visualizar'
                : 'Pré-visualização indisponível para este tipo de arquivo. Baixe o arquivo para abri-lo.'
            }
            icon={<EyeOutlined />}
            onClick={() => onPreview(material.preview_url)}
            disabled={!previewable}
          />
          <IconActionButton
            label="Baixar"
            icon={<DownloadOutlined />}
            onClick={() => onDownload(material.id)}
          />
          <IconActionButton
            label="Excluir"
            icon={<DeleteOutlined />}
            onClick={() => onDelete(material.id)}
            danger
          />
        </div>
      </div>
    </article>
  );
};

const ALERT_CLASS_NAME = [
  '!px-4 !py-2 !items-center',
  '[&_.ant-alert-message]:!mb-0',
  '[&_.ant-alert-description]:!text-gray-400'
].join(' ');

interface PublicationNoticeProps {
  publication: SupplementaryMaterialsPublication;
  materialsCount: number;
}

const PublicationNotice = ({
  publication,
  materialsCount
}: PublicationNoticeProps) => {
  const { published, isLoading, onPublish, onUnpublish } = publication;

  const showUnpublishConfirm = useCallback(() => {
    confirm({
      title: 'Despublicar materiais institucionais?',
      icon: <ExclamationCircleOutlined className="!text-red-500" />,
      content:
        'A aba de materiais institucionais deixará de ser exibida na área do aluno deste curso, mesmo que existam materiais cadastrados.',
      okText: 'Despublicar',
      cancelText: 'Cancelar',
      okType: 'danger',
      okButtonProps: { type: 'primary' },
      onOk: onUnpublish
    });
  }, [onUnpublish]);

  if (published) {
    return (
      <Alert
        className={ALERT_CLASS_NAME}
        type={materialsCount === 0 ? 'warning' : 'success'}
        showIcon
        message="Disponível para os alunos"
        description={
          materialsCount === 0
            ? 'A aba materiais institucionais está visível na área do aluno, mesmo sem nenhum material cadastrado. Adicione materiais ou despublique para ocultá-la.'
            : 'A aba materiais institucionais está visível na área do aluno.'
        }
        action={
          <Button loading={isLoading} onClick={showUnpublishConfirm}>
            Despublicar
          </Button>
        }
      />
    );
  }

  if (materialsCount === 0) {
    return null;
  }

  return (
    <Alert
      className={ALERT_CLASS_NAME}
      type="warning"
      showIcon
      message="Ainda não disponível para os alunos"
      description="Publique para exibir a aba materiais institucionais na área do aluno."
      action={
        <Button
          loading={isLoading}
          onClick={onPublish}
          className="!border-green-600 !bg-white !text-green-600 hover:!border-green-500 hover:!text-green-500"
        >
          Publicar
        </Button>
      }
    />
  );
};

export const SupplementaryMaterialsPanel = ({
  parentKind,
  parentReference,
  variant,
  publication
}: SupplementaryMaterialsPanelProps) => {
  const copy = COPY[variant];
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState<ISupplementaryMaterial[]>([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const { modalProps: previewModalProps, show: previewModalShow } = useModal(
    {}
  );
  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createModalShow,
    close: createModalClose
  } = useModalForm({
    resource: 'supplementary_materials',
    action: 'create',
    warnWhenUnsavedChanges: false
  });

  const { mutateAsync: deleteMaterial } = useDelete<ISupplementaryMaterial>();

  const getSavedItems = useCallback(() => {
    setLoading(true);
    listSupplementaryMaterials({
      filter: { parent_reference: parentReference },
      items: 100
    })
      .then(res => setMaterials(res))
      .finally(() => setLoading(false));
  }, [parentReference]);

  useEffect(() => {
    getSavedItems();
  }, [getSavedItems]);

  const handlePreview = useCallback(
    (fileUrl: string) => {
      setPreviewUrl(fileUrl);
      previewModalShow();
    },
    [previewModalShow]
  );

  const handleDownload = useCallback((id: string) => {
    downloadSupplementaryMaterials(id).then(res => window.open(res.url));
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteMaterial({
        resource: 'supplementary_materials',
        id,
        successNotification: () => ({
          type: 'success',
          description: 'Tudo certo!',
          message: copy.deleteSuccess
        }),
        errorNotification: () => ({
          type: 'error',
          description: 'Algo deu errado!',
          message: copy.deleteError
        })
      });
      getSavedItems();
    },
    [copy.deleteError, copy.deleteSuccess, deleteMaterial, getSavedItems]
  );

  const showDeleteConfirm = useCallback(
    (id: string) => {
      confirm({
        title: 'Tem certeza que deseja excluir este material?',
        icon: <ExclamationCircleOutlined className="!text-red-500" />,
        content: 'Esta ação não poderá ser desfeita.',
        okText: 'Excluir',
        cancelText: 'Cancelar',
        okType: 'danger',
        okButtonProps: { type: 'primary' },
        onOk() {
          return handleDelete(id);
        }
      });
    },
    [handleDelete]
  );

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="m-0 text-sm text-gray-500">{copy.description}</p>
      </header>

      {publication && !loading && (
        <PublicationNotice
          publication={publication}
          materialsCount={materials.length}
        />
      )}

      <section>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="m-0 text-base font-semibold text-gray-900">
              Materiais cadastrados
            </h3>
            <Tag className="m-0">{materials.length}</Tag>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => createModalShow()}
          >
            {copy.addButtonText}
          </Button>
        </div>

        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : materials.length === 0 ? (
          <p className="m-0 rounded-xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
            Nenhum material publicado ainda.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {materials.map(material => (
              <MaterialCard
                key={material.id}
                material={material}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onDelete={showDeleteConfirm}
              />
            ))}
          </div>
        )}
      </section>

      <SupplementarMaterialUploadModal
        key="UploadFile"
        formProps={createFormProps}
        modalProps={createModalProps}
        closeModal={createModalClose}
        getSavedItems={getSavedItems}
        parentReference={parentReference}
        parentKind={parentKind}
        okText={copy.uploadOkText}
        successMessage={copy.uploadSuccess}
        restrictToPdf={variant === 'institutional'}
      />

      <ContentPreviewModal
        modalProps={previewModalProps}
        previewUrl={previewUrl}
      />
    </div>
  );
};
